/**
 * The editor — Tiptap over Yjs, per §5.1 and §8.3.
 *
 * ## What this component is responsible for, and what it is not
 *
 * It edits. It does **not** save. §8.3 puts the persistence somewhere else on
 * purpose — "Yjs holds live state; snapshots to the JSON column on debounce" —
 * so there is no PATCH in this file and no autosave timer. Every keystroke goes
 * into the `Y.Doc`, the provider ships it, and `apps/collab` persists it. An
 * editor that also wrote through the REST route would be a second write path,
 * and ADR 0002 allows exactly one.
 *
 * ## The property that matters most
 *
 * Every block carries a stable id, minted by `UniqueID` inside
 * `documentExtensions` and never reassigned. §5.1 hangs anchored comments
 * (§8.1), block-level links (§6.6), and retrieval citations (§9.4) off those
 * ids; §12's phase test is literally "does it require Yjs or stable block IDs to
 * exist?". None of that ships in 1a — but the ids do, so 1b is a feature and not
 * a migration. Nothing in this file may reassign one, which is why nothing in
 * this file constructs a block: insertions come from `insertions.ts` without
 * ids, and the extension assigns them once, on entry.
 *
 * ## Reading versus editing
 *
 * `editable: false` renders the same schema with no provider at all. That is not
 * a UI nicety: the collab socket authorizes `document.edit` (§8.3's
 * `onAuthenticate`), so a reader who opened one would be refused and would then
 * reconnect forever. A reader gets `initialContent` — the body the API resolved
 * for them, which for someone without a working draft is the published version
 * (§7.4).
 */

import type { JSONContent } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import { type ReactElement, useEffect, useRef } from 'react';
import { useMe } from '../api/queries.ts';
import { Alert } from '../components/ui/feedback.tsx';
import { useCollabSession } from './collab-session.ts';
import { CommentMargin } from './comments/index.ts';
import { ConnectionIndicator, connectionState } from './connection.tsx';
import { EditorStyles } from './editor-styles.tsx';
import { collaborationExtensions, editorExtensions } from './extensions.ts';
import { type PresenceFace, presenceColor } from './identity.ts';
import { DocumentToolbar } from './toolbar.tsx';

/**
 * The key under which a seeded document records that it has been seeded.
 *
 * It lives inside the `Y.Doc`, so it is CRDT state that persists with the
 * document and is visible to every client — which is the only place a flag like
 * this can live without two browsers each deciding they are the first.
 */
const SEED_MAP = 'matchbox';
const SEED_FLAG = 'seeded';

export interface DocumentEditorProps {
  nodeId: string;
  /** False renders the editor read-only — a reader resolving to the published version (§7.4). */
  editable: boolean;
  /** The body to show when collaboration is not connected, from GET /api/documents/:id. */
  initialContent: Record<string, unknown>;
  /** Fired after Yjs state settles, for the caller's save indicator. Never on every keystroke. */
  onDirtyChange?: (dirty: boolean) => void;
}

export function DocumentEditor({
  nodeId,
  editable,
  initialContent,
  onDirtyChange,
}: DocumentEditorProps): ReactElement {
  const me = useMe();
  const user = me.data?.user;

  const identity: PresenceFace | null = user
    ? { personId: user.id, name: user.name, color: presenceColor(user.id) }
    : null;

  const session = useCollabSession(nodeId, { connect: editable, identity });
  const { doc, provider } = session;
  const collaborative = Boolean(doc && provider && identity);

  const editor = useEditor(
    {
      editable,
      extensions: [
        ...editorExtensions(),
        ...(doc && provider && identity
          ? collaborationExtensions({ document: doc, provider, user: identity })
          : []),
      ],
      // Content is set here **only** when there is no `Y.Doc`. Under
      // collaboration the document's contents are whatever Yjs says they are,
      // and handing Tiptap an initial document as well would insert it on top of
      // the synced state — the classic doubled-document bug.
      ...(collaborative ? {} : { content: initialContent as JSONContent }),
      // No `role` is set: ProseMirror's element is `contenteditable`, which
      // assistive technology already understands as a rich editing region, and
      // `role="textbox"` would flatten the structure it exposes.
      editorProps: { attributes: { class: 'mx-document-body', 'aria-label': 'Document body' } },
    },
    // The editor is rebuilt when the collaboration inputs change, because
    // `Collaboration` binds to one `Y.Doc` at construction. `identity` is in the
    // list for the same reason: the caret is configured with the person, and
    // `GET /auth/me` can resolve after the first render.
    [nodeId, editable, doc, provider, identity?.personId],
  );

  useSeededContent({ editor, session, editable, initialContent });
  useDirtyReporting({ dirty: session.unsynced > 0, collaborative, onDirtyChange });

  const state = connectionState(session);
  const blocked = editable && session.status === 'denied';

  useEffect(() => {
    // A read-only surface follows its prop. `useEditor` sets `content` once, at
    // construction, so without this a caller switching what it is showing — a
    // version preview, or a different revision of the same node — would keep
    // rendering the first body it was handed. Never while collaborating: there
    // the document is whatever Yjs says it is, and writing a prop over it would
    // overwrite a co-author mid-sentence.
    if (collaborative) return;
    editor.commands.setContent(initialContent as JSONContent, { emitUpdate: false });
  }, [editor, collaborative, initialContent]);

  useEffect(() => {
    // Typing into a refused session produces edits that will never be shipped
    // anywhere, so a denial takes the keyboard away and the alert below says
    // why. Done here rather than through the `editable` option because
    // `useEditor` with a non-empty dependency list does not re-apply options —
    // it only rebuilds when a dependency changes, and rebuilding the editor to
    // change one boolean would throw away the ProseMirror state for nothing.
    editor.setEditable(editable && !blocked, false);
  }, [editor, editable, blocked]);

  return (
    <div className="flex flex-col gap-3">
      <EditorStyles />

      {editable ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <DocumentToolbar editor={editor} isDisabled={blocked} />
          <ConnectionIndicator snapshot={session} />
        </div>
      ) : null}

      {state.detail ? (
        <Alert tone={state.tone === 'error' ? 'error' : 'warning'}>{state.detail}</Alert>
      ) : null}

      <EditorContent editor={editor} className="mx-document" />

      {/* §8.1's margin, mounted for readers as well as editors: a client may
          comment on a deliverable they cannot edit, and `canEdit` is what
          distinguishes the two — it gates §8.2's Apply and the editorial half of
          resolving, nothing else. It is here rather than on the page because a
          comment anchors to a block id and a character range in *this* editor's
          document, and the only honest way to compute one is from the live
          ProseMirror state. `pendingSync` is passed because an anchor is
          validated against the stored draft, which trails the last few
          keystrokes by a debounce (§8.3). */}
      <CommentMargin
        editor={editor}
        nodeId={nodeId}
        canEdit={editable && !blocked}
        pendingSync={session.unsynced > 0}
      />
    </div>
  );
}

/**
 * Puts the API's copy of the body into an empty shared document, once.
 *
 * This exists because nothing on the server does it: `apps/collab`'s
 * `onLoadDocument` replays `yjs_update` rows and stops, so a document created
 * through `POST /api/documents` — content in the JSON column, no Yjs rows yet —
 * opens as a blank `Y.Doc`. Without this, every document written before its
 * first editing session appears empty in the editor.
 *
 * Three conditions, all necessary:
 *
 * 1. **After `synced`.** Seeding before the handshake means seeding into a
 *    document whose contents have not arrived, which is how you get two copies.
 * 2. **The fragment is empty.**
 * 3. **The flag inside the `Y.Doc` is unset**, and setting it is what makes this
 *    idempotent across reconnects and across clients — it travels with the
 *    document rather than living in this tab.
 *
 * A race remains: two clients that connect to a never-seeded document in the
 * same instant can both pass all three tests and insert the body twice. The real
 * fix is server-side, in `onLoadDocument`, where there is exactly one reader and
 * no race at all. That is reported as a gap rather than papered over here.
 */
function useSeededContent({
  editor,
  session,
  editable,
  initialContent,
}: {
  editor: ReturnType<typeof useEditor>;
  session: ReturnType<typeof useCollabSession>;
  editable: boolean;
  initialContent: Record<string, unknown>;
}): void {
  const { doc, provider } = session;

  // Held in a ref so that a caller who rebuilds the object every render does not
  // resubscribe to `synced` every render. The value is only ever read inside the
  // seed, and only once.
  const body = useRef(initialContent);
  body.current = initialContent;

  useEffect(() => {
    if (!editor || !doc || !provider || !editable) return;

    const seed = () => {
      if (editor.isDestroyed) return;
      const meta = doc.getMap<boolean>(SEED_MAP);
      if (meta.get(SEED_FLAG)) return;
      if (doc.getXmlFragment('default').length > 0) {
        // Content arrived from the server: record that this document no longer
        // needs seeding, so a later empty moment (everything deleted) is not
        // mistaken for a document that was never populated.
        meta.set(SEED_FLAG, true);
        return;
      }
      meta.set(SEED_FLAG, true);
      editor.commands.setContent(body.current as JSONContent, { emitUpdate: true });
    };

    if (provider.synced) seed();
    provider.on('synced', seed);
    return () => {
      provider.off('synced', seed);
    };
  }, [editor, doc, provider, editable]);
}

/**
 * Reports the save state as a boolean that flips, not as a stream.
 *
 * The prop's contract is "never on every keystroke", so the callback fires only
 * when the answer changes. `unsyncedChanges` is the provider's own count of
 * edits the server has not acknowledged, which is the honest definition of
 * dirty here — the editor does not save, so "dirty" can only mean "not yet
 * shipped".
 */
function useDirtyReporting({
  dirty,
  collaborative,
  onDirtyChange,
}: {
  dirty: boolean;
  collaborative: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}): void {
  const reported = useRef<boolean | null>(null);

  useEffect(() => {
    if (!onDirtyChange) return;
    // A read-only surface is never dirty; reporting `false` for it would still
    // be a claim about a save state that does not exist.
    if (!collaborative) return;
    if (reported.current === dirty) return;
    reported.current = dirty;
    onDirtyChange(dirty);
  }, [dirty, collaborative, onDirtyChange]);
}
